import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { Card } from './ui/card';
import { clientsData } from '../data/mock';

const Clients = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      align: 'start',
      skipSnaps: false,
      dragFree: false
    },
    [Autoplay({ delay: 2500, stopOnInteraction: false })]
  );

  const [imageErrors, setImageErrors] = useState({});

  const handleImageError = (clientId) => {
    setImageErrors(prev => ({ ...prev, [clientId]: true }));
  };

  return (
    <section id="clients" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            {clientsData.title}
          </h2>
          <p className="text-xl text-slate-600">
            {clientsData.caption}
          </p>
        </div>

        {/* Clients Carousel */}
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-6">
            {clientsData.clients.map((client, index) => (
              <div
                key={`client-${client.id}-${index}`}
                className="flex-[0_0_auto] w-[180px]"
              >
                <Card className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-slate-200 hover:border-red-400 bg-white overflow-hidden h-full">
                  <div className="aspect-square flex flex-col items-center justify-center p-6 relative">
                    {/* Logo Circle or Image */}
                    {client.logoImage && !imageErrors[client.id] ? (
                      <div className="w-20 h-20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                        <img 
                          src={client.logoImage} 
                          alt={`${client.name} logo`}
                          className="max-w-full max-h-full object-contain"
                          onError={() => handleImageError(client.id)}
                        />
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-red-500/20">
                        <span className="text-2xl font-bold text-white">
                          {client.logo}
                        </span>
                      </div>
                    )}
                    
                    {/* Client Name */}
                    <h3 className="text-sm font-semibold text-slate-900 text-center leading-snug line-clamp-2">
                      {client.name}
                    </h3>
                    
                    {/* Category Badge */}
                    <span className="mt-2 text-xs text-red-700 bg-red-50 px-2 py-1 rounded-full text-center">
                      {client.category}
                    </span>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Hint */}
        <div className="text-center mt-8 text-sm text-slate-500">
          <p className="flex items-center justify-center gap-2">
            <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            Auto-scrolling carousel • Drag to explore
          </p>
        </div>

        {/* Stats Section */}
        <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
          <div className="text-center p-6 bg-white rounded-2xl shadow-md border border-slate-200 hover:shadow-lg transition-shadow">
            <div className="text-4xl font-bold text-red-600 mb-2">50+</div>
            <div className="text-slate-600 font-medium">Active Clients</div>
          </div>
          <div className="text-center p-6 bg-white rounded-2xl shadow-md border border-slate-200 hover:shadow-lg transition-shadow">
            <div className="text-4xl font-bold text-red-600 mb-2">13+</div>
            <div className="text-slate-600 font-medium">Industries Served</div>
          </div>
          <div className="text-center p-6 bg-white rounded-2xl shadow-md border border-slate-200 hover:shadow-lg transition-shadow">
            <div className="text-4xl font-bold text-slate-900 mb-2">98%</div>
            <div className="text-slate-600 font-medium">Client Satisfaction</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Clients;
